import React, { useState } from "react";
import styled from "styled-components";
import Loader from "./core-components/Loader.tsx";
import { useSelector } from "react-redux";
import { translations } from "../../utils/translations.js";

const Wrapper = styled.div`
  padding: 20px;

  display: flex;
  flex-direction: column;

  flex: 1;
`;

const ItemContainer = styled.div`
  display: grid;
  grid-gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  grid-auto-rows: 1fr; /* Ensures all rows are of equal height */
  align-items: stretch; /* Makes all items in a row stretch to match the tallest one */

  @media (min-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); /* Tablets */
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(
      auto-fill,
      minmax(250px, 1fr)
    ); /* Larger screens */
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const PageButton = styled.button`
  padding: 5px 10px;
  margin: 0 5px;
  background-color: var(--fill-green);
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:disabled {
    background-color: #c6c6c6;
    cursor: not-allowed;
  }
`;

const NoData = styled.h3`
  display: flex;
  flex: 1;
  justify-content: center;
  margin-top: 40px;
`;

interface PaginatedItemsProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode; // Function to render each item
  maxItemsPerPage: number;
  isLoading?: boolean;
}

const PaginatedItems = <T extends {}>({
  items,
  renderItem,
  maxItemsPerPage = 30,
  isLoading = false,
}: PaginatedItemsProps<T>) => {
  const languageCode = useSelector((state: any) => state.settings.languageCode);
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate the range of items for the current page
  const indexOfLastItem = currentPage * maxItemsPerPage;
  const indexOfFirstItem = indexOfLastItem - maxItemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);

  // Pagination logic
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Calculate total pages
  const totalPages = Math.ceil(items.length / maxItemsPerPage);

  if (isLoading) {
    return <Loader />;
  }
  if (!items || (items.length === 0 && !isLoading)) {
    return <NoData>{translations[languageCode]["no-data"]}</NoData>;
  }

  return (
    <Wrapper data-testid="paginated-container">
      <ItemContainer>
        {currentItems.map((item, index) => (
          <div data-testid="item" key={index}>
            {renderItem(item)}
          </div>
        ))}
      </ItemContainer>

      <Pagination>
        <PageButton
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </PageButton>
        {[...Array(totalPages)].map((_, index) => (
          <PageButton
            key={index}
            onClick={() => paginate(index + 1)}
            disabled={currentPage === index + 1}
          >
            {index + 1}
          </PageButton>
        ))}
        <PageButton
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </PageButton>
      </Pagination>
    </Wrapper>
  );
};

export default PaginatedItems;
